import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Buildings, Globe } from "@phosphor-icons/react";

/**
 * Super admin school context switcher.
 * "__all__" = global view (default). Otherwise scopes ALL API calls to that school_id
 * via the X-School-Context header (see /app/frontend/src/lib/api.js interceptor).
 * Changing context does a hard reload so all pages re-fetch with the new scope.
 */
export default function SchoolContextSwitcher() {
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [ctx, setCtx] = useState(localStorage.getItem("kosyam_school_context") || "__all__");

  useEffect(() => {
    if (user?.role !== "super_admin") return;
    // Fetch all schools (super_admin bypasses school filter)
    api.get("/schools").then(r => setSchools(r.data || [])).catch(() => {});
  }, [user?.role]);

  if (user?.role !== "super_admin") return null;

  const onChange = (v) => {
    setCtx(v);
    if (v === "__all__") localStorage.removeItem("kosyam_school_context");
    else localStorage.setItem("kosyam_school_context", v);
    // Hard reload so all pages re-fetch with the new scope
    window.location.reload();
  };

  const current = schools.find(s => s.id === ctx);

  return (
    <div className="flex items-center gap-2" data-testid="school-context-switcher">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold hidden sm:block">
        Vue
      </span>
      <Select value={ctx} onValueChange={onChange}>
        <SelectTrigger className="h-8 w-full sm:w-44 text-xs border-accent/40 bg-accent/5" data-testid="school-context-trigger">
          <div className="flex items-center gap-1.5 truncate">
            {ctx === "__all__"
              ? <><Globe size={12} weight="duotone" className="text-accent shrink-0" /><span className="truncate">Toutes les écoles</span></>
              : <><Buildings size={12} weight="duotone" className="text-accent shrink-0" /><span className="truncate">{current?.name || ctx.slice(0, 8)}</span></>
            }
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__" data-testid="school-context-all">
            <span className="inline-flex items-center gap-2"><Globe size={12} weight="duotone" /> Toutes les écoles</span>
          </SelectItem>
          {schools.map(s => (
            <SelectItem key={s.id} value={s.id} data-testid={`school-context-${s.id}`}>
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.primary_color || "#1a2a6c" }} />
                {s.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
